import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowUpRight, Cpu, Leaf, ShieldCheck, Building2 } from "lucide-react";

const PARTNER_VIDEO =
  "https://customer-assets.emergentagent.com/job_royal-bautraeger/artifacts/axlia9er_ECO%20BuildingTechnikGmbH%20%281%29.mp4";

const PARTNERS = {
  wellbeing: {
    title: "Well-Being ConTech",
    domain: "wellbeingcontech.com",
    href: "https://wellbeingcontech.com/",
    image: "/images/wellbeing-hero.jpg",
  },
  ukraine: {
    title: "Ukraine Rebuilding Alliance",
    domain: "ukrainerebuild.com.ua",
    href: "https://www.ukrainerebuild.com.ua/",
    image: "/images/ukraine-alliance-hero.jpg",
  },
  buildingtechnik: {
    title: "ECO Building Technik",
    domain: "eco-building.tech",
    href: "https://www.eco-building.tech/",
    image:
      "https://images.unsplash.com/photo-1770843244931-9e6d4d1055be?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    logo:
      "https://customer-assets.emergentagent.com/job_royal-bautraeger/artifacts/0pjcrg1b_LOGO-ECO%20Rechnung.jpg",
  },
};

const COPY = {
  wellbeing: {
    de: {
      eyebrow: "Technologie-Partner",
      title1: "Well-Being",
      title2: "ConTech.",
      lede:
        "Progressive Bautechnologie für gesundes, energieeffizientes Wohnen. Als Technologie- und Vorstandspartner von Royalhouse entsteht ein direkter Draht zwischen Forschung und gebauten Projekten.",
      story_eyebrow: "Auftrag",
      story_title: "Bauen, das Menschen guttut.",
      story_body:
        "Well-Being ConTech entwickelt Materialien, Bauteile und smarte Systeme, die Innenraumluft, Akustik, Tageslicht und Energieeffizienz optimieren. Die Entwicklungen fließen direkt in unsere Royalhouse-Projekte ein — vom Vorfertigungsmodul bis zur Smart-Home-Steuerung.",
      pillars: [
        { title: "Gesunde Materialien", text: "Schadstoffarme Baustoffe, geprüfte Emissionen, verbesserte Innenraumluft." },
        { title: "Energie & Klima", text: "Hochwertige Dämmung, Lüftungswärmerückgewinnung, PV-ready, Smart-Home-Integration." },
        { title: "Smart Home", text: "Zigbee-basierte Steuerung von Licht, Klima und Zutritt — aus einer Hand." },
      ],
      cta: "Mehr auf wellbeingcontech.com",
    },
    en: {
      eyebrow: "Technology partner",
      title1: "Well-Being",
      title2: "ConTech.",
      lede:
        "Progressive construction technology for healthy, energy-efficient living. As a technology and board-level partner of Royalhouse, there is a direct line between R&D and built projects.",
      story_eyebrow: "Mission",
      story_title: "Buildings that are good for people.",
      story_body:
        "Well-Being ConTech develops materials, components and smart systems that optimise indoor air, acoustics, daylight and energy efficiency. Their R&D flows directly into our Royalhouse projects — from prefab modules to smart-home controls.",
      pillars: [
        { title: "Healthy materials", text: "Low-emission building materials, certified VOCs, improved indoor air quality." },
        { title: "Energy & climate", text: "High-grade insulation, heat-recovery ventilation, PV-ready, smart-home integration." },
        { title: "Smart home", text: "Zigbee-based control of lighting, climate and access — single source of truth." },
      ],
      cta: "Learn more at wellbeingcontech.com",
    },
    ru: {
      eyebrow: "Технологический партнёр",
      title1: "Well-Being",
      title2: "ConTech.",
      lede:
        "Прогрессивные строительные технологии для здорового и энергоэффективного жилья. Как технологический и правленческий партнёр Royalhouse — прямая связь между R&D и реализованными объектами.",
      story_eyebrow: "Миссия",
      story_title: "Здания, которые полезны людям.",
      story_body:
        "Well-Being ConTech разрабатывает материалы, компоненты и умные системы, оптимизирующие микроклимат, акустику, естественный свет и энергоэффективность. Эти разработки напрямую применяются в наших проектах Royalhouse — от префабрикации до умного дома.",
      pillars: [
        { title: "Здоровые материалы", text: "Низкоэмиссионные стройматериалы, сертифицированные летучие вещества, улучшенный воздух." },
        { title: "Энергия и климат", text: "Эффективная теплоизоляция, рекуперация, PV-ready, интеграция умного дома." },
        { title: "Умный дом", text: "Управление светом, климатом и доступом по Zigbee — из одного интерфейса." },
      ],
      cta: "Подробнее на wellbeingcontech.com",
    },
  },
  ukraine: {
    de: {
      eyebrow: "Initiativen-Partner",
      title1: "Ukraine Rebuilding",
      title2: "Alliance.",
      lede:
        "Eine Allianz aus Bauunternehmen, Investoren und Fachleuten zum Wiederaufbau der Ukraine. Royalhouse bringt sich als Bautechnologie-Partner mit Strategie und Vorfertigungs-Know-how ein.",
      story_eyebrow: "Aufgabe",
      story_title: "Wiederaufbau mit Standard.",
      story_body:
        "Die Ukraine Rebuilding Alliance koordiniert Industrie, Politik und internationale Partner für einen schnellen, qualitativ hochwertigen Wiederaufbau. Royalhouse liefert das Bausystem: BIM-basiert, vorgefertigt, rückverfolgbar.",
      team_eyebrow: "Vorstand & Aufsichtsrat",
      team_title: "Menschen hinter der Allianz.",
      cta: "Mehr auf ukrainerebuild.com.ua",
    },
    en: {
      eyebrow: "Initiative partner",
      title1: "Ukraine Rebuilding",
      title2: "Alliance.",
      lede:
        "An alliance of construction companies, investors and experts working on Ukraine's reconstruction. Royalhouse contributes as a construction-technology partner with strategy and prefabrication know-how.",
      story_eyebrow: "Mandate",
      story_title: "Reconstruction with standards.",
      story_body:
        "The Ukraine Rebuilding Alliance coordinates industry, government and international partners to deliver fast, high-quality reconstruction. Royalhouse contributes the building system: BIM-based, prefabricated, traceable.",
      team_eyebrow: "Board & Supervisory",
      team_title: "People behind the alliance.",
      cta: "Learn more at ukrainerebuild.com.ua",
    },
    ru: {
      eyebrow: "Партнёр инициативы",
      title1: "Ukraine Rebuilding",
      title2: "Alliance.",
      lede:
        "Альянс строительных компаний, инвесторов и специалистов для восстановления Украины. Марк Кестельбойм отвечает за стратегию и маркетинг — Royalhouse участвует как технологический партнёр.",
      story_eyebrow: "Задача",
      story_title: "Восстановление по стандарту.",
      story_body:
        "Ukraine Rebuilding Alliance координирует промышленность, государство и международных партнёров для быстрого и качественного восстановления. Royalhouse предоставляет строительную систему: BIM, префабрикация, прослеживаемость.",
      team_eyebrow: "Правление и наблюдательный совет",
      team_title: "Люди за альянсом.",
      cta: "Подробнее на ukrainerebuild.com.ua",
    },
  },
  buildingtechnik: {
    de: {
      eyebrow: "Haustechnik-Partner · KI-Systemintegrator",
      title1: "ECO Building",
      title2: "Technik.",
      lede:
        "ECO Building Technik plant, liefert und integriert KI-optimierte Gebäudeautomation mit A+++ Wärmepumpen- und Energiesystemen. Heizung, Warmwasser und Strom werden datenbasiert geregelt, automatisch optimiert und dauerhaft energieeffizient betrieben — bis zu 30 % geringerer Energieverbrauch durch adaptive Steuerung.",
      story_eyebrow: "Spezialisierung",
      story_title: "KI-Systemintegrator, kein Installationsbetrieb.",
      story_body:
        "ECO Building Technik übernimmt die vollständige technische Verantwortung: Systemanalyse und Architekturplanung, Auswahl der KI-optimierten A+++ Geräte, Integration über KNX, BACnet oder Modbus, permanentes Monitoring und laufende Optimierung. Adaptive Algorithmen erkennen ineffiziente Laufzeiten, Lastspitzen und thermische Speicherverluste in Echtzeit und passen die Regelparameter dynamisch an.",
      pillars: [
        { title: "KI-Heizungssteuerung", text: "A+++ Wärmepumpen mit adaptiver Regelung, vorausschauender Lastoptimierung und automatischer Anpassung an PV-Erträge und variable Tarife." },
        { title: "Energiemanagement", text: "Strom- und Lastmanagement, Spitzenlastreduktion, Eigenverbrauchsoptimierung. Echtzeit-Verbrauchsdaten und Performance-Monitoring." },
        { title: "BIM-Systemplanung", text: "Sämtliche Anlagen werden im digitalen Gebäudemodell vollständig abgebildet. Strukturierte Datengrundlage für Planung, Integration und Optimierung." },
      ],
      cta: "Mehr auf eco-building.tech",
    },
    en: {
      eyebrow: "MEP partner · AI system integrator",
      title1: "ECO Building",
      title2: "Technik.",
      lede:
        "ECO Building Technik plans, delivers and integrates AI-optimised building automation with A+++ heat pump and energy systems. Heating, hot water and electricity are controlled in real time, automatically optimised and operated with lasting efficiency — up to 30 % lower energy consumption through adaptive control.",
      story_eyebrow: "Specialisation",
      story_title: "AI system integrator, not an installation company.",
      story_body:
        "ECO Building Technik takes full technical responsibility: system analysis and architecture planning, selection of AI-optimised A+++ devices, integration via KNX, BACnet or Modbus, permanent monitoring and continuous optimisation. Adaptive algorithms detect inefficient runtimes, peak loads and thermal storage losses in real time and dynamically adjust control parameters.",
      pillars: [
        { title: "AI heating control", text: "A+++ heat pumps with adaptive control, predictive load optimisation and automatic alignment with PV yields and dynamic tariffs." },
        { title: "Energy management", text: "Electricity and load management, peak-load reduction, self-consumption optimisation. Real-time consumption data and performance monitoring." },
        { title: "BIM system planning", text: "All technical systems mapped completely in a digital building model. Structured data basis for planning, integration and optimisation." },
      ],
      cta: "Learn more at eco-building.tech",
    },
    ru: {
      eyebrow: "Партнёр по инженерным системам · AI-интегратор",
      title1: "ECO Building",
      title2: "Technik.",
      lede:
        "ECO Building Technik проектирует, поставляет и интегрирует AI-оптимизированную автоматизацию зданий с тепловыми насосами и энергосистемами класса A+++. Отопление, ГВС и электричество регулируются на основе данных, автоматически оптимизируются и работают с постоянной эффективностью — снижение энергопотребления до 30 % за счёт адаптивного управления.",
      story_eyebrow: "Специализация",
      story_title: "AI-системный интегратор, не установочная фирма.",
      story_body:
        "ECO Building Technik берёт на себя полную техническую ответственность: системный анализ и архитектурное планирование, подбор AI-оптимизированных устройств A+++, интеграция через KNX, BACnet или Modbus, постоянный мониторинг и непрерывная оптимизация. Адаптивные алгоритмы обнаруживают неэффективные циклы, пиковые нагрузки и тепловые потери в реальном времени и динамически корректируют параметры регулирования.",
      pillars: [
        { title: "AI-управление отоплением", text: "Тепловые насосы A+++ с адаптивным управлением, прогнозной оптимизацией нагрузки и автоматическим согласованием с PV и тарифами." },
        { title: "Энергоменеджмент", text: "Управление электричеством и нагрузкой, сглаживание пиков, максимизация самопотребления. Данные потребления в реальном времени." },
        { title: "BIM-планирование", text: "Все системы полностью отражены в цифровой модели здания. Структурированная основа для планирования, интеграции и оптимизации." },
      ],
      cta: "Подробнее на eco-building.tech",
    },
  },
};

const BUILDINGTECHNIK_KPIS = [
  { value: "15", label_de: "Laufende Projekte", label_en: "Active projects", label_ru: "Текущие проекты", label_uk: "Поточні проєкти" },
  { value: "596", label_de: "Wärmepumpen-Projekte", label_en: "Heat pump projects", label_ru: "Проектов с тепловыми насосами", label_uk: "Проєкти з тепловими насосами" },
  { value: "1 Mio.", label_de: "Realisierte m²", label_en: "Realised m²", label_ru: "Реализованных м²", label_uk: "Реалізованих м²" },
  { value: "56", label_de: "Business-Partner", label_en: "Business partners", label_ru: "Бизнес-партнёров", label_uk: "Бізнес-партнерів" },
];

const BUILDINGTECHNIK_SEGMENTS = {
  de: [
    { title: "Einfamilienhäuser", text: "Maximale Energieeinsparung durch KI-optimierte Wärmepumpensysteme. Zentral vernetzt, per App in Echtzeit steuerbar." },
    { title: "Mehrfamilienhäuser", text: "Skalierbare Energiemanagement-Systeme mit stabil kalkulierbaren Betriebskosten. Geeignet für Neubau und Sanierung." },
    { title: "Wohnanlagen mit BIM", text: "Digitale Systemplanung, präzise Auslegung, langfristige Wertstabilität. Echtzeit-Optimierung von Erzeugung, Verteilung und Verbrauch." },
    { title: "Wohnungen", text: "Sensorik erfasst Temperatur, Feuchte und Verbrauch in Echtzeit. KI-Steuerlogik optimiert Wärmepumpe, Heizkreise und Warmwasser direkt auf Wohnungsebene." },
  ],
  en: [
    { title: "Detached houses", text: "Maximum energy savings via AI-optimised heat pump systems. Centrally networked, app-controlled in real time." },
    { title: "Multi-family houses", text: "Scalable energy management with stable, calculable operating costs. Suitable for new build and refurbishment." },
    { title: "BIM residential", text: "Digital system planning, precise sizing, long-term value stability. Real-time optimisation of generation, distribution and consumption." },
    { title: "Apartments", text: "Sensors capture temperature, humidity and consumption in real time. AI logic optimises heat pump, heating circuits and hot water at apartment level." },
  ],
  ru: [
    { title: "Частные дома", text: "Максимальная экономия энергии за счёт AI-оптимизированных тепловых насосов. Центральное управление через приложение в реальном времени." },
    { title: "Многоквартирные дома", text: "Масштабируемые системы энергоменеджмента со стабильно прогнозируемыми эксплуатационными расходами." },
    { title: "Жилые комплексы с BIM", text: "Цифровое планирование, точный расчёт, долгосрочная стабильность стоимости. Оптимизация выработки, распределения и потребления в реальном времени." },
    { title: "Квартиры", text: "Сенсоры фиксируют температуру, влажность и потребление в реальном времени. AI-логика оптимизирует тепловой насос и ГВС на уровне квартиры." },
  ],
  uk: [
    { title: "Приватні будинки", text: "Максимальна економія енергії завдяки AI-оптимізованим тепловим насосам. Централізоване керування через додаток у реальному часі." },
    { title: "Багатоквартирні будинки", text: "Масштабовані системи енергоменеджменту зі стабільно прогнозованими експлуатаційними витратами." },
    { title: "Житлові комплекси з BIM", text: "Цифрове планування, точний розрахунок, довгострокова стабільність вартості. Оптимізація генерації, розподілу та споживання в реальному часі." },
    { title: "Квартири", text: "Сенсори фіксують температуру, вологість і споживання в реальному часі. AI-логіка оптимізує тепловий насос і ГВП на рівні квартири." },
  ],
};

const URA_TEAM_BY_LANG = {
  de: [
    { name: "Yuriy Maslov", role: "Vorstandsvorsitzender" },
    { name: "Mykola Kutsak", role: "Vorstand · Bau & Entwicklung" },
    { name: "Pavlo Unguryan", role: "Aufsichtsratsvorsitzender" },
    { name: "Veniamin Unguryan", role: "Stv. Vorstandsvorsitzender" },
    { name: "Mykyta Laktionov", role: "Stv. Vorstandsvorsitzender" },
    { name: "Natalia Perevalova", role: "Vorstandsmitglied" },
    { name: "Sergey Diablo", role: "Stv. Aufsichtsratsvorsitzender" },
    { name: "Ihor Babych", role: "International Relations & Finanzen" },
  ],
  en: [
    { name: "Yuriy Maslov", role: "Head of the Board" },
    { name: "Mykola Kutsak", role: "Board Member · Construction & Development" },
    { name: "Pavlo Unguryan", role: "Head of the Supervisory Board" },
    { name: "Veniamin Unguryan", role: "Deputy Chairman of the Management Board" },
    { name: "Mykyta Laktionov", role: "Deputy Chairman of the Management Board" },
    { name: "Natalia Perevalova", role: "Board Member" },
    { name: "Sergey Diablo", role: "Deputy Chairman of the Supervisory Board" },
    { name: "Ihor Babych", role: "International Relations & Finances" },
  ],
  ru: [
    { name: "Yuriy Maslov", role: "Председатель правления" },
    { name: "Mykola Kutsak", role: "Член правления · Строительство" },
    { name: "Pavlo Unguryan", role: "Председатель набл. совета" },
    { name: "Veniamin Unguryan", role: "Зам. председателя правления" },
    { name: "Mykyta Laktionov", role: "Зам. председателя правления" },
    { name: "Natalia Perevalova", role: "Член правления" },
    { name: "Sergey Diablo", role: "Зам. председателя набл. совета" },
    { name: "Ihor Babych", role: "Междунар. отношения и финансы" },
  ],
};

export default function PartnerGenericPage({ partnerKey }) {
  const { lang, t } = useLang();
  const partner = PARTNERS[partnerKey];
  const c = COPY[partnerKey][lang] || COPY[partnerKey].de;
  const URA_TEAM = URA_TEAM_BY_LANG[lang] || URA_TEAM_BY_LANG.ru || URA_TEAM_BY_LANG.de;

  return (
    <div data-testid={`${partnerKey}-page`} className="pt-32">
      <PageMeta pageKey={partnerKey === "wellbeing" ? "wellbeing" : partnerKey === "buildingtechnik" ? "buildingtechnik" : "ukraine"} />
      {/* Hero */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.eyebrow}</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {c.title1} <br /><span className="italic">{c.title2}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          {c.lede}
        </p>
      </section>

      {/* Media strip — video for ECO Building Technik (start at s=8), image for others */}
      {partnerKey === "buildingtechnik" ? (
        <PartnerVideo videoUrl={PARTNER_VIDEO} startAt={8} testId="buildingtechnik-video" poster={partner.image} />
      ) : partner.logo ? (
        <section className="border-y border-white/10 bg-white">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24 flex items-center justify-center">
            <img
              src={partner.logo}
              alt={`${partner.title} Logo`}
              className="max-h-48 md:max-h-64 w-auto object-contain"
              data-testid={`${partnerKey}-logo`}
            />
          </div>
        </section>
      ) : (
        <section className="border-y border-white/10">
          <div className="aspect-[21/9] overflow-hidden">
            <img src={partner.image} alt={partner.title} className="w-full h-full object-cover" />
          </div>
        </section>
      )}

      {/* Story */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.story_eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">
            {c.story_title}
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-12 space-y-6">
          <p className="text-base md:text-lg text-white/70 leading-relaxed font-light">
            {c.story_body}
          </p>
          <a
            href={partner.href}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`${partnerKey}-external-link`}
            className="rh-link inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium pt-4"
          >
            {c.cta} <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* Pillars (wellbeing & buildingtechnik) */}
      {c.pillars && (
        <section className="py-24 md:py-32 bg-[#0A2244]/40 border-y border-white/10">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
              {c.pillars.map((p, i) => {
                const Icon = [Leaf, ShieldCheck, Cpu][i] || Building2;
                return (
                  <div key={p.title} className="border-b border-r border-white/10 p-8 md:p-10">
                    <div className="flex items-start justify-between mb-8">
                      <span className="font-display text-3xl text-white/30">0{i + 1}</span>
                      <Icon size={28} strokeWidth={1.25} />
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-4 leading-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-white/65 leading-relaxed">{p.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* KPIs & object segments — buildingtechnik only */}
      {partnerKey === "buildingtechnik" && (
        <>
          <section className="py-20 md:py-28 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10" data-testid="buildingtechnik-kpis">
              {BUILDINGTECHNIK_KPIS.map((k) => {
                const label = k[`label_${lang}`] || k.label_de;
                return (
                  <div key={k.value} className="bg-[#051630] p-8 md:p-10">
                    <p className="font-display text-4xl md:text-6xl font-light tracking-tight leading-none mb-3">
                      {k.value}
                    </p>
                    <p className="text-[11px] tracking-[0.25em] uppercase text-white/55 leading-[1.5]">
                      {label}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="py-20 md:py-28 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 border-t border-white/10">
            {(() => {
              const SEGMENTS_COPY = {
                de: { eyebrow: "Objekt-Segmente", title: "Lösungen für jede Objektgröße." },
                en: { eyebrow: "Object segments", title: "Solutions for every scale." },
                ru: { eyebrow: "Сегменты объектов", title: "Решения для любого масштаба." },
                uk: { eyebrow: "Сегменти об'єктів", title: "Рішення для будь-якого масштабу." },
              };
              const sc = SEGMENTS_COPY[lang] || SEGMENTS_COPY.de;
              return (
                <>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">{sc.eyebrow}</p>
                  <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">
                    {sc.title}
                  </h2>
                </>
              );
            })()}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
              {(BUILDINGTECHNIK_SEGMENTS[lang] || BUILDINGTECHNIK_SEGMENTS.de).map((s, i) => (
                <div key={s.title} className="bg-[#051630] p-8 md:p-10">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">0{i + 1}</span>
                  <h3 className="font-display text-lg uppercase tracking-tight mt-4 mb-3">{s.title}</h3>
                  <p className="text-sm text-white/70 leading-[1.7] font-light">{s.text}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Ukraine Alliance team */}
      {partnerKey === "ukraine" && (
        <section className="py-24 md:py-32 bg-[#0A2244]/40 border-y border-white/10">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.team_eyebrow}</p>
            <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">
              {c.team_title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
              {URA_TEAM.map((m, i) => (
                <div key={m.name} className="border-b border-r border-white/10 p-8 md:p-10">
                  <span className="font-display text-2xl text-white/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-6">
                    {m.name}
                  </h3>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mt-3">
                    {m.role}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#051630] relative overflow-hidden">
        <div className="absolute inset-0 rh-grid-bg opacity-40" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("home.cta.eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light uppercase tracking-tight leading-[1.02]">
              {partner.title}. <br /><span className="italic">+</span> Royalhouse.
            </h2>
          </div>
          <div className="md:col-span-4">
            <Link
              to="/kontakt"
              className="rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white w-full md:w-auto"
              data-testid={`${partnerKey}-cta-kontakt`}
            >
              <span>{t("nav.cta")}</span>
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PartnerVideo({ videoUrl, startAt = 0, testId, poster }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const start = () => {
      try {
        v.currentTime = startAt;
        v.play().catch((err) => console.warn("Partner video autoplay blocked:", err?.message));
      } catch (err) {
        console.warn("Partner video start failed:", err?.message);
      }
    };
    if (v.readyState >= 1) start();
    else v.addEventListener("loadedmetadata", start, { once: true });
  }, [startAt]);

  return (
    <section className="border-y border-white/10 bg-[#051630]">
      <div className="relative aspect-video md:aspect-[21/9] overflow-hidden">
        <video
          ref={ref}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          data-testid={testId}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: "rgba(5, 22, 48, 0.25)" }} />
      </div>
    </section>
  );
}

